
import { useCallback, useRef } from "react";
import { GraphData, BoundingBox } from "../useGraphData";

interface UseCenterViewProps {
  graphRef: React.MutableRefObject<any>;
}

export const useCenterView = ({ graphRef }: UseCenterViewProps) => {
  const hasAppliedInitialCentering = useRef(false);
  const currentOperation = useRef<"init" | "manual-center" | null>(null);
  const lastRenderDimensions = useRef({ width: 0, height: 0 });

  const centerView = useCallback((graphData: GraphData) => {
    if (!graphRef.current || !graphData.nodes.length) {
      console.log("Cannot center view: graphRef or nodes are missing");
      return;
    }
    
    if (currentOperation.current === "manual-center") {
      console.log("Manual centering operation - skipping auto scaling");
      return;
    }
    
    try {
      console.log(`Calculating bounding box for ${graphData.nodes.length} nodes`);
      
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      let validNodesCount = 0;
      
      graphData.nodes.forEach(node => {
        if (node.x !== undefined && node.y !== undefined) {
          minX = Math.min(minX, node.x);
          minY = Math.min(minY, node.y);
          maxX = Math.max(maxX, node.x);
          maxY = Math.max(maxY, node.y);
          validNodesCount++;
        }
      });
      
      if (validNodesCount === 0) {
        console.log("No nodes with valid positions found, using default centering");
        if (graphRef.current) {
          graphRef.current.centerAt(0, 0, 300);
          return null;
        }
        return null;
      }
      
      const boundingBox = { minX, minY, maxX, maxY };
      
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      
      if (!graphRef.current) return null;
      
      console.log(`Centering at (${centerX}, ${centerY})`);
      
      graphRef.current.centerAt(centerX, centerY, 300);
      
      hasAppliedInitialCentering.current = true;
      console.log("Initial centering applied successfully");
      
      lastRenderDimensions.current = {
        width: graphRef.current._canvas.parentElement.clientWidth || window.innerWidth,
        height: graphRef.current._canvas.parentElement.clientHeight || window.innerHeight
      };
      
      return boundingBox;
    } catch (err) {
      console.error("Error during centerView:", err);
      
      if (graphRef.current) {
        console.log("Using fallback centering approach");
        try {
          graphRef.current.centerAt(0, 0, 300);
        } catch (fallbackErr) {
          console.error("Fallback centering also failed:", fallbackErr);
        }
      }
      return null;
    }
  }, [graphRef]);

  const handleCenterView = useCallback((graphData: GraphData, applyInitialZoom: () => void) => {
    console.log("Center View button clicked");
    
    if (!graphRef.current) {
      console.log("Cannot center view: graphRef is missing");
      return;
    }
    
    try {
      currentOperation.current = "manual-center";
      
      let centerX = 0, centerY = 0;
      let validNodesCount = 0;
      
      graphData.nodes.forEach(node => {
        if (node.x !== undefined && node.y !== undefined) {
          centerX += node.x;
          centerY += node.y;
          validNodesCount++;
        }
      });
      
      if (validNodesCount > 0) {
        centerX /= validNodesCount;
        centerY /= validNodesCount;
      }
      
      console.log(`Manual center: Centering at (${centerX}, ${centerY})`);
      
      graphRef.current.centerAt(centerX, centerY, 300);
      
      setTimeout(() => {
        applyInitialZoom();
        
        setTimeout(() => {
          currentOperation.current = null;
        }, 200);
      }, 350);
    } catch (e) {
      console.error("Error during manual centering:", e);
      currentOperation.current = null;
    }
  }, [graphRef]);

  const drawBoundingBox = useCallback((boundingBox: BoundingBox | null) => {
    if (!graphRef.current || !boundingBox) return;
    
    try {
      const canvas = graphRef.current._canvas;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.beginPath();
      ctx.rect(
        boundingBox.minX, 
        boundingBox.minY, 
        boundingBox.maxX - boundingBox.minX, 
        boundingBox.maxY - boundingBox.minY
      );
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 4;
      ctx.stroke();
    } catch (err) {
      console.error("Error drawing bounding box:", err);
    }
  }, [graphRef]);

  return {
    centerView,
    handleCenterView,
    drawBoundingBox,
    hasAppliedInitialCentering,
    currentOperation,
    lastRenderDimensions
  };
};
